/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { GlobalStyles, ThemeProvider } from '@mui/material';
import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Profile from './components/Profile/Profile';
import Spinner from './components/Spinner/Spinner';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import AccountSettingsSkeleton from './routes/AccountSettings/components/AccountSettingsSkeleton';
import ForbiddenError403 from './routes/errors/Forbidden';
import NotFound404 from './routes/errors/NotFound';
import Home from './routes/Home/Home';
import Navigation from './routes/Navigation/Navigation';
import Trips from './routes/Trips/Trips';

import 'react-day-picker/dist/style.css';
import { HelmetProvider } from 'react-helmet-async';
import { onAuthStateChangedListener, toAuthUser } from './firebase/auth.utils';
import { syncUserDocument } from './firebase/users.utils';
import { selectCurrentUser, selectIsAuthInitialized } from './redux/user/user.selectors';
import { setCurrentUser, setDisplayName } from './redux/user/user.slice';
import PasswordProtect from './routes/PasswordProtect/PasswordProtect';
import { BASE_GLOBAL_STYLES, ROOT_TOKEN_STYLES } from './theme/globalStyles';
import { DEFAULT_THEME } from './theme/muiTheme';

// Auth/settings pages load their form libraries on demand, outside the main trip-board chunk.
const AccountSettings = lazy(() => import('./routes/AccountSettings/AccountSettings'));
const SignInAndSignUp = lazy(() => import('./routes/SignInAndSignUp/SignInAndSignUp'));

function App() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const isAuthenticated = localStorage.getItem('isAuthenticated') || false;

  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener((user) => {
      dispatch(setCurrentUser(user ? toAuthUser(user) : null));
      if (user) {
        syncUserDocument(user)
          .then((displayName) => {
            if (displayName) {
              dispatch(setDisplayName(displayName));
            }
          })
          // Non-fatal: the user can keep using the app, and the sync retries on next load.
          .catch(() => undefined);
      }
    });

    return unsubscribe;
  }, [dispatch]);

  return (
    <>
      {/* Token custom properties must sit above the auth branch: the
          PasswordProtect gate renders outside the ThemeProvider. */}
      <GlobalStyles styles={ROOT_TOKEN_STYLES} />
      {!isAuthenticated ? (
        <PasswordProtect />
      ) : (
        <ThemeProvider theme={DEFAULT_THEME}>
          <GlobalStyles styles={BASE_GLOBAL_STYLES} />
          <HelmetProvider>
            <Routes>
              <Route
                path='/'
                element={<Navigation />}
              >
                <Route
                  index
                  element={<Home />}
                />
                <Route
                  path='profile'
                  element={<Profile />}
                />
                <Route
                  path='trips/*'
                  element={<Trips />}
                />
                <Route
                  path='login'
                  element={
                    !isAuthInitialized ? (
                      <Spinner />
                    ) : currentUser ? (
                      <Navigate
                        replace
                        to='/trips'
                      />
                    ) : (
                      <Suspense fallback={<Spinner />}>
                        <SignInAndSignUp />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path='account-settings'
                  element={
                    !isAuthInitialized ? (
                      <Spinner />
                    ) : currentUser ? (
                      <Suspense fallback={<AccountSettingsSkeleton />}>
                        <AccountSettings />
                      </Suspense>
                    ) : (
                      <Navigate
                        replace
                        to='/login'
                      />
                    )
                  }
                />
                <Route
                  path='/forbidden'
                  element={<ForbiddenError403 />}
                />
                <Route
                  path='*'
                  element={<NotFound404 />}
                />
              </Route>
            </Routes>
          </HelmetProvider>
        </ThemeProvider>
      )}
    </>
  );
}

App.displayName = 'App';
export default App;
